import React, { useCallback, useRef, useState } from 'react';
import ViewSwitcher from './ViewSwitcher';
import HistoryPanel from './HistoryPanel';
import ViewContainer from './ViewContainer';
import CampaignPickerModal from '../FlowchartView/components/CampaignPickerModal';
import AddMissionArrow from '../shared/AddMissionArrow';
import VariablesPanel from '../../VariablesPanel';
import SprintDetailModals from './SprintDetailModals';
import useFokusMode from '../shared/useFokusMode';

/**
 * Presentational shell for SprintDetail: toolbars, views, panels, and modals.
 */
const SprintDetailContent = ({
  isLoadingContent,
  isMultiCampaignMode,
  localSprint,
  allSprints,
  multiCampaignData,
  onSelectSprint,
  sidebarCollapsed,
  undoStack,
  redoStack,
  undo,
  redo,
  undoTo,
  redoTo,
  undoAll,
  clearHistory,
  state,
  setup,
  actions,
  handlers,
  modals,
  nested,
  fokusModeRefs,
  wrappedToolbar,
  handleViewRename,
  handleViewIconChangeLocal,
  handleCampaignPickerSelect,
  handleDeleteTasksWithNestedCheck,
  handleTrashNestedSprint,
  calculatedProgress,
  flowProps,
  augmentedKanbanProps,
  augmentedTableProps,
  todoListProps,
  viewSwitcherProps,
  neuralFilteredTasks,
  inProgressLockWarningModal,
  renameFieldModal,
  setRenameFieldModal,
  handleDeleteModalConfirm,
  renderHandlers,
  isVariablesPanelOpen,
  setIsVariablesPanelOpen,
  variableCampaign,
  handleCampaignVariablesChange,
  handleInheritedVariablePatch,
  handleCampaignVariablesColumnWidthsChange,
  openMission,
  closeMission,
  setMissionDocStack,
  restoredDocStack,
  skipSurfaceAnimation,
  consumeSkipSurfaceAnimation,
  tabOpenMissionId,
  missionDocDirect = false,
  lockStates,
}) => {
  const { tasks, statuses } = setup;

  const {
    showArchived,
    showActionableOnly,
    showNestedCampaigns,
    kanbanSelectedIds,
    setKanbanSelectedIds,
    tableSelectedIds,
    setTableSelectedIds,
    ganttSelectedIds,
    setGanttSelectedIds,
    todoSelectedIds,
    setTodoSelectedIds,
    neuralSelectedIds,
    setNeuralSelectedIds,
  } = state;

  const neuralViewApiRef = useRef(null);
  const [boardSelectedIds, setBoardSelectedIds] = useState(() => new Set());
  const boardFokus = useFokusMode({
    tasks,
    statuses,
    nestedSprints: nested?.nestedSprints || [],
    selectedTaskIds: boardSelectedIds,
  });
  const [showAddMissionArrow, setShowAddMissionArrow] = useState(false);
  const handleShowAddMissionArrow = useCallback(() => {
    setShowAddMissionArrow(true);
  }, []);
  const handleDismissAddMissionArrow = useCallback(() => {
    setShowAddMissionArrow(false);
  }, []);

  return (
    <div className="sprint-detail">
      <ViewSwitcher
        {...viewSwitcherProps}
        neuralViewApiRef={neuralViewApiRef}
        boardFokus={boardFokus}
        boardSelectedIds={boardSelectedIds}
        onManageVariables={
          isMultiCampaignMode ? null : () => setIsVariablesPanelOpen(true)
        }
      />
      <VariablesPanel
        isOpen={isVariablesPanelOpen}
        onClose={() => setIsVariablesPanelOpen(false)}
        campaign={variableCampaign}
        onCampaignVariablesChange={handleCampaignVariablesChange}
        onPatchInheritedVariable={handleInheritedVariablePatch}
        allSprints={allSprints}
        columnWidths={localSprint?.variablesColumnWidths || null}
        onColumnWidthsChange={handleCampaignVariablesColumnWidthsChange}
      />
      <HistoryPanel
        undoStack={undoStack}
        redoStack={redoStack}
        onUndo={undo}
        onRedo={redo}
        onUndoTo={undoTo}
        onRedoTo={redoTo}
        onUndoAll={undoAll}
        onClear={clearHistory}
        isOpen={state.isHistoryOpen}
        onClose={() => state.setIsHistoryOpen(false)}
        sidebarCollapsed={sidebarCollapsed}
      />
      <ViewContainer
        isLoadingContent={isLoadingContent}
        currentView={wrappedToolbar.currentView}
        tasks={tasks}
        statuses={statuses}
        localSprint={localSprint}
        calculatedProgress={calculatedProgress}
        showArchived={showArchived}
        showActionableOnly={showActionableOnly}
        showNestedCampaigns={showNestedCampaigns}
        boardFilters={state.filtersPerView?.board || {}}
        flowProps={flowProps}
        kanbanProps={augmentedKanbanProps}
        tableProps={augmentedTableProps}
        onShowAddMissionArrow={handleShowAddMissionArrow}
        neuralCardDisplaySettings={setup.cardDisplaySettingsPerView?.neural}
        neuralFilteredTasks={neuralFilteredTasks}
        neuralViewApiRef={neuralViewApiRef}
        ganttSwimlane={wrappedToolbar.ganttSwimlane}
        ganttSortSetting={localSprint?.sortSettingsPerView?.gantt || 'manual'}
        ganttFilters={state.filtersPerView?.gantt || {}}
        breadcrumbPath={isMultiCampaignMode ? [] : nested.breadcrumbPath}
        onSelectSprint={onSelectSprint}
        onSprintIconChange={isMultiCampaignMode ? handleViewIconChangeLocal : setup.handleLocalSprintIconChange}
        onRenameSprint={isMultiCampaignMode ? handleViewRename : handlers.handleRenameSprint}
        onTaskDateRangeChange={actions.handleTaskDateRangeChange}
        onOpenDetails={openMission}
        allTags={setup.allTags}
        customFields={localSprint?.customFields || []}
        todoListProps={todoListProps}
        nestedSprints={nested.nestedSprints}
        allSprints={allSprints}
        lockStates={lockStates}
        kanbanSelectedIds={kanbanSelectedIds}
        onKanbanSelectionChange={setKanbanSelectedIds}
        tableSelectedIds={tableSelectedIds}
        onTableSelectionChange={setTableSelectedIds}
        ganttSelectedIds={ganttSelectedIds}
        onGanttSelectionChange={setGanttSelectedIds}
        todoSelectedIds={todoSelectedIds}
        onTodoSelectionChange={setTodoSelectedIds}
        neuralSelectedIds={neuralSelectedIds}
        onNeuralSelectionChange={setNeuralSelectedIds}
        ganttFokus={fokusModeRefs.ganttFokus}
        todoFokus={fokusModeRefs.todoFokus}
        neuralFokus={fokusModeRefs.neuralFokus}
        boardSelectedIds={boardSelectedIds}
        onBoardSelectionChange={setBoardSelectedIds}
        boardFokus={boardFokus}
        onGanttAddTask={renderHandlers.onGanttAddTask}
        onGanttAddDecision={renderHandlers.onGanttAddDecision}
        onGanttAddWait={renderHandlers.onGanttAddWait}
        onGanttDecisionAnswerChange={actions.handleDecisionAnswerChange}
        onGanttDecisionOptionsChange={actions.handleDecisionOptionsChange}
        onGanttRenameTask={actions.handleRenameTask}
        onGanttDeleteTasks={handleDeleteTasksWithNestedCheck}
        onGanttPasteTasks={handlers.handlePasteTasks}
        onGanttDueDateChange={actions.handleDueDateChange}
        bulkActions={{
          onStatusChange: actions.handleTaskStatusChange,
          onPriorityChange: actions.handlePriorityChange,
          onIconChange: actions.handleIconChange,
          onSetPrimaryTag: actions.handleSetPrimaryTag,
          onAddTag: actions.handleAddTag,
          onRemoveTag: actions.handleRemoveTag,
          onArchiveTasks: actions.handleArchiveTasks,
          onUnarchiveTask: actions.handleUnarchiveTask,
          onDueDateChange: actions.handleDueDateChange,
          onEstimatedDurationMinutesChange: actions.handleEstimatedDurationMinutesChange,
          onStartDateChange: actions.handleTaskStartDateChange,
          onCustomFieldValueChange: actions.handleCustomFieldValueChange,
        }}
      />
      <CampaignPickerModal
        isOpen={state.isCampaignPickerOpen}
        campaigns={multiCampaignData?.campaigns || []}
        onSelect={handleCampaignPickerSelect}
        onCancel={() => state.setIsCampaignPickerOpen(false)}
        mode={state.campaignPickerMode}
      />
      <SprintDetailModals
        isLoadingContent={isLoadingContent}
        isMultiCampaignMode={isMultiCampaignMode}
        localSprint={localSprint}
        allSprints={allSprints}
        onSelectSprint={onSelectSprint}
        state={state}
        actions={actions}
        handlers={handlers}
        modals={modals}
        nested={nested}
        wrappedToolbar={wrappedToolbar}
        handleDeleteModalConfirm={handleDeleteModalConfirm}
        handleTrashNestedSprint={handleTrashNestedSprint}
        renderHandlers={renderHandlers}
        variableCampaign={variableCampaign}
        handleCampaignVariablesChange={handleCampaignVariablesChange}
        handleInheritedVariablePatch={handleInheritedVariablePatch}
        closeMission={closeMission}
        setMissionDocStack={setMissionDocStack}
        restoredDocStack={restoredDocStack}
        skipSurfaceAnimation={skipSurfaceAnimation}
        consumeSkipSurfaceAnimation={consumeSkipSurfaceAnimation}
        tabOpenMissionId={tabOpenMissionId}
        missionDocDirect={missionDocDirect}
        lockStates={lockStates}
        inProgressLockWarningModal={inProgressLockWarningModal}
        renameFieldModal={renameFieldModal}
        setRenameFieldModal={setRenameFieldModal}
        tasks={tasks}
        statuses={statuses}
      />
      <AddMissionArrow
        isVisible={showAddMissionArrow}
        onDismiss={handleDismissAddMissionArrow}
      />
    </div>
  );
};

export default SprintDetailContent;
